import { useRef } from 'react';
import type { ExtraConfig } from '@gov-nx/core/app';
import { useLocale } from '@gov-nx/core/service';
import {
	ProfilROSReklamaceContextProvider,
	RosComplaintVariant,
	useProfilROSDetailContext,
} from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	ContentLayout,
	DataTable,
	Dialog,
	Icon,
	LayoutGap,
} from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';
import { ProfileROSComplaintSubmitButton } from '../ProfileROSComplaintSubmitButton';
import type { ProfileROSComplaintViewRef } from '../ProfileROSComplaintView';
import { ProfileROSComplaintView } from '../ProfileROSComplaintView';
import { EstablishmentsListModal } from './EstablishmentsListModal';
import { MembersListModal } from './MembersListModal';
import { PersonsListModal } from './PersonsListModal';

export const PODataView = (
	props: ExtraConfig['profileRoutes']['rosDetailConfig']['rosDetail']['PODataView']
) => {
	const {
		isMembersListModalOpen,
		isEstablishmentsListModalOpen,
		isPersonsListModalOpen,
		myInvolvement,
		members,
		establishments,
		persons,
		detailData,
		controls,
		setControls,
		code,
	} = useProfilROSDetailContext();
	const { t } = useLocale(code);
	const submitComplaintRef = useRef<ProfileROSComplaintViewRef>(null);

	return (
		<LayoutGap gap="m">
			{myInvolvement.map((person) => (
				<ContentLayout key={person.ico}>
					<LayoutGap>
						<DataTable responsive>
							<tbody>
								<tr>
									<td>{t('zaznam.angazovanost')}</td>
									<td>
										<strong>{t(`zaznam.role.hodnota.${person.role}`)}</strong>
									</td>
								</tr>
							</tbody>
						</DataTable>
						<p className="text-body-s">{t('zaznam.angazovanost-popis')}</p>
					</LayoutGap>
				</ContentLayout>
			))}

			<ContentLayout>
				<LayoutGap gap="m">
					<DataTable responsive>
						<tbody>
							<tr>
								<td>{t('zaznam.jmeno')}</td>
								<td>
									<strong>
										{detailData?.nazevSpolecnosti
											? detailData.nazevSpolecnosti
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.ico')}</td>
								<td>
									<strong>{detailData?.ico ? detailData.ico : '-'}</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.pravni-forma')}</td>
								<td>
									<strong>
										{detailData?.pravniForma?.nazev
											? detailData.pravniForma.nazev
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.agenda')}</td>
								<td>
									<strong>
										{detailData?.agenda?.nazev ? detailData.agenda.nazev : '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.sidlo')}</td>
								<td>
									<strong>
										{detailData?.sidloSpolecnosti?.adresaText
											? detailData.sidloSpolecnosti.adresaText
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.pocatek-registrace-vznik')}</td>
								<td>
									<strong>
										{detailData?.datumVzniku
											? toDateReadable(detailData.datumVzniku)
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td>{t('zaznam.konec-registrace-zanik')}</td>
								<td>
									<strong>
										{detailData?.datumZaniku
											? toDateReadable(detailData.datumZaniku)
											: t('zaznam.datum-zaniku-ne')}
									</strong>
								</td>
							</tr>
							<tr className="border-b-0">
								<td>{t('zaznam.pozastaveni-preruseni')}</td>
								<td>
									<strong>
										{detailData?.pozastaveniPreruseni
											? t('zaznam.pozastaveni-preruseni-ano')
											: t('zaznam.pozastaveni-preruseni-ne')}
									</strong>
								</td>
							</tr>
							<tr className="border-b-0">
								<td colSpan={2}>
									<p className="text-body-xs! text-(--text-secondary)">
										{t('zaznam.pozastaveni-preruseni-popis')}
									</p>
								</td>
							</tr>
							{members.length ? (
								<tr>
									<td>{t('zaznam.struktura-statutarniho-organu')}</td>
									<td>
										<strong>{t('zaznam.seznam-clenu')}</strong>
										<Button
											onClick={isMembersListModalOpen.setTrue}
											color="primary"
											type="base"
											iconStart={
												<Icon
													type="components"
													name="eye"
												/>
											}
											size="s"
										/>
										<MembersListModal />
									</td>
								</tr>
							) : null}
							{establishments.length ? (
								<tr>
									<td>{t('zaznam.provozovny')}</td>
									<td>
										<strong>{t('zaznam.seznam-provozoven')}</strong>
										<Button
											onClick={isEstablishmentsListModalOpen.setTrue}
											color="primary"
											type="base"
											iconStart={
												<Icon
													type="components"
													name="eye"
												/>
											}
											size="s"
										/>
										<EstablishmentsListModal />
									</td>
								</tr>
							) : null}

							{detailData?.dorucovaciAdresa?.adresaText ? (
								<>
									<tr className="border-b-0">
										<td>{t('zaznam.dorucovaci-adresa-popis')}</td>
										<td>
											<strong>{detailData.dorucovaciAdresa.adresaText}</strong>
										</td>
									</tr>
									<tr className="border-b-0">
										<td colSpan={2}>
											<p className="text-body-xs! text-(--text-secondary)">
												{t('zaznam.dorucovaci-adresa-vysvetleni')}
											</p>
										</td>
									</tr>
								</>
							) : null}

							<tr>
								<td>{t('zaznam.datova-schranka')}</td>
								<td>
									<strong>
										{detailData?.datovaSchrankaId
											? detailData.datovaSchrankaId
											: '-'}
									</strong>
								</td>
							</tr>
							{persons.length ? (
								<tr>
									<td>{t('zaznam.angazovane-osoby')}</td>
									<td>
										<strong>{t('zaznam.seznam-osob')}</strong>
										<Button
											onClick={isPersonsListModalOpen.setTrue}
											color="primary"
											type="base"
											size="s"
											iconStart={
												<Icon
													type="components"
													name="eye"
												/>
											}
										/>
										<PersonsListModal />
									</td>
								</tr>
							) : null}
						</tbody>
					</DataTable>
					{props.isComplaintCtaVisible ? (
						<>
							<ButtonGroupLayout>
								<Button
									onClick={() =>
										setControls({
											complaintModal: detailData,
											complaintVariant: RosComplaintVariant.AGENDA,
										})
									}
									color="primary"
									type="outlined"
									size="l">
									{t('akce.podnet-k-oprave-udaju')}
								</Button>
							</ButtonGroupLayout>
							{controls.complaintModal && (
								<ProfilROSReklamaceContextProvider
									code={code}
									data={controls.complaintModal}
									variant={controls.complaintVariant}
									onComplaintSubmit={() =>
										setControls({ complaintModal: null })
									}>
									<Dialog
										isOpen={!!controls.complaintModal}
										onClose={() =>
											setControls({
												complaintModal: detailData,
												complaintVariant: RosComplaintVariant.AGENDA,
											})
										}
										title={<h3>{t('formular.podnet-k-oprave-udaju')}</h3>}
										accessibleCloseLabel="akce.wcag.zavrit-formular"
										footer={
											<ProfileROSComplaintSubmitButton
												onClick={() => submitComplaintRef.current?.submit()}
											/>
										}>
										<ProfileROSComplaintView
											code={code}
											ref={submitComplaintRef}
										/>
									</Dialog>
								</ProfilROSReklamaceContextProvider>
							)}
						</>
					) : null}
				</LayoutGap>
			</ContentLayout>
		</LayoutGap>
	);
};
